import { useState, useRef } from 'react';
import {
  updateWatchReference,
  deleteWatchReference,
  getCustomCalibres,
  deriveFunctionsFromCalibre,
} from '../../services/firebase';
import type { CustomCalibreEntry } from '../../services/firebase';
import {
  buildCalibreIdIndex,
  findRefCalibreEntry,
  resolveDisplayCalibre,
} from '../../services/calibreResolution';
import { buildCalibreBulkUpdate } from './calibreBulkUpdate';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { LookupFieldSelect } from './LookupFieldSelect';
import { LookupMultiSelect } from './LookupMultiSelect';
import { CustomCalibreModal } from './CustomCalibreModal';
import { BulkAIEnrich } from './BulkAIEnrich';
import { GroupAsVariantsDialog } from './GroupAsVariantsDialog';
import { UngroupVariantsDialog } from './UngroupVariantsDialog';
import type { WatchBrand, WatchReference } from '../../types';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';

// Bulk-update field shapes:
//   'string'        — free-text input, written to the field path as a trimmed string
//   'number'        — numeric input, coerced to a number before writing
//   'lookup'        — single-select dropdown sourced from `lookupCollection`
//   'lookup-multi'  — multi-select (chips) sourced from `lookupCollection`, written as
//                     string[] (or a comma-joined string when `multiAsString` is set)
// `paths` lists the Firestore field path(s) to write. Dot-paths (e.g. 'case.material')
// update a single nested field via updateDoc without clobbering its siblings; some
// fields fan out to several aliases the codebase keeps in sync (see caseSize).
type BulkFieldType = 'string' | 'number' | 'lookup' | 'lookup-multi';

interface BulkUpdateFieldDef {
  field: string;
  label: string;
  type: BulkFieldType;
  paths?: string[];
  lookupCollection?: string;
  // For 'lookup-multi': when true the selection is serialized to a comma-joined
  // string on write (matching how ReferenceEditor stores dialAndHands.color)
  // instead of a string[]. Defaults to writing a string[].
  multiAsString?: boolean;
}

const BULK_UPDATE_FIELDS: readonly BulkUpdateFieldDef[] = [
  { field: 'collection', label: 'Collection', type: 'string' },
  { field: 'movementType', label: 'Movement Type', type: 'string' },
  {
    field: 'watchType',
    label: 'Watch Type',
    type: 'lookup',
    lookupCollection: 'lookup_watch_types',
    paths: ['watchType'],
  },
  { field: 'calibre', label: 'Calibre', type: 'string' },
  { field: 'series', label: 'Series', type: 'string' },
  { field: 'nickname', label: 'Nickname', type: 'string' },
  {
    field: 'dialColor',
    label: 'Dial Color',
    type: 'lookup-multi',
    lookupCollection: 'lookup_dial_colors',
    paths: ['dialAndHands.color'],
    // ReferenceEditor stores dial color as a comma-joined string, not an array.
    multiAsString: true,
  },
  {
    field: 'dialStyle',
    label: 'Dial Style',
    type: 'lookup',
    lookupCollection: 'lookup_dial_styles',
    paths: ['dialStyle'],
  },
  {
    // Single-value lookup picker; writes the canonical finish slug to
    // dialAndHands.finish (the field the dialColor backfill also populates).
    field: 'dialFinish',
    label: 'Dial Finish',
    type: 'lookup',
    lookupCollection: 'lookup_dial_finish',
    paths: ['dialAndHands.finish'],
  },
  {
    // Hands COLOR — multi-select. ReferenceEditor stores it as a comma-joined
    // string at dialAndHands.handsColor, so serialize the selection the same way.
    field: 'handsColor',
    label: 'Hands Color',
    type: 'lookup-multi',
    lookupCollection: 'lookup_hand_colors',
    paths: ['dialAndHands.handsColor'],
    multiAsString: true,
  },
  {
    // Marker TYPE — how the markers are constructed. Multi-select, written as
    // a string[] (NOT multiAsString: these two fields are arrays in the schema,
    // unlike the comma-joined handsColor/indexColor/handTypes below). Stored at
    // dialAndHands.indexes under its historical name because the RN app reads
    // that key; it moved off lookup_dial_numerals in the marker-type split.
    field: 'indexes',
    label: 'Marker Type',
    type: 'lookup-multi',
    lookupCollection: 'lookup_dial_marker_type',
    paths: ['dialAndHands.indexes'],
  },
  {
    // Marker STYLE — what the markers are. Keeps lookup_dial_numerals.
    field: 'numerals',
    label: 'Numerals',
    type: 'lookup-multi',
    lookupCollection: 'lookup_dial_numerals',
    paths: ['dialAndHands.numerals'],
  },
  {
    // Hands — single-value hand STYLE (Dauphine/Baton/…), not color or function.
    field: 'hands',
    label: 'Hands',
    type: 'lookup',
    lookupCollection: 'lookup_dial_hands',
    paths: ['dialAndHands.hands'],
  },
  {
    // Index COLOR — multi-select (NEW, Phase 1). Comma-joined string at
    // dialAndHands.indexColor, mirroring handsColor above.
    field: 'indexColor',
    label: 'Index Color',
    type: 'lookup-multi',
    lookupCollection: 'lookup_index_colors',
    paths: ['dialAndHands.indexColor'],
    multiAsString: true,
  },
  {
    // Hand TYPES — multi-select (NEW, Phase 1). Functional hand types, comma-
    // joined string at dialAndHands.handTypes, mirroring handsColor above.
    field: 'handTypes',
    label: 'Hand Type',
    type: 'lookup-multi',
    lookupCollection: 'lookup_hand_types',
    paths: ['dialAndHands.handTypes'],
    multiAsString: true,
  },
  {
    field: 'caseMaterial',
    label: 'Case Material',
    type: 'lookup',
    lookupCollection: 'lookup_case_materials',
    paths: ['case.material'],
  },
  {
    // Same lookup + path the ReferenceEditor's "Shape" picker uses, so a
    // bulk-set shape is the identical slug a hand-edited one would be.
    field: 'caseShape',
    label: 'Case Shape',
    type: 'lookup',
    lookupCollection: 'lookup_case_shapes',
    paths: ['case.shape'],
  },
  {
    field: 'strapMaterial',
    label: 'Strap Material',
    type: 'lookup',
    lookupCollection: 'lookup_strap_materials',
    paths: ['strap.material'],
  },
  {
    // `bezel` IS the bezel material — the short name predates the lookup and
    // every reader is on it, so the label says material and the path does not.
    field: 'bezelMaterial',
    label: 'Bezel Material',
    type: 'lookup',
    lookupCollection: 'lookup_bezel_material',
    paths: ['bezel'],
  },
  {
    // Multi-select, and stored as a real ARRAY (no multiAsString) — the
    // crownType/crystal convention, not the comma-joined one dialColor uses.
    field: 'bezelColors',
    label: 'Bezel Colors',
    type: 'lookup-multi',
    lookupCollection: 'lookup_bezel_colors',
    paths: ['bezelColors'],
  },
  {
    // ReferenceEditor edits water resistance via the lookup_water_resistance
    // dropdown (string values like "100m"), stored at case.waterResistance.
    field: 'waterResistance',
    label: 'Water Resistance',
    type: 'lookup',
    lookupCollection: 'lookup_water_resistance',
    paths: ['case.waterResistance'],
  },
  {
    // ReferenceEditor writes case size to all three aliases and reads them in
    // priority order; mirror that here so the value shows up everywhere.
    field: 'caseSize',
    label: 'Case Size (mm)',
    type: 'number',
    paths: ['case.diameter', 'case.diameterMm', 'case.sizeMm'],
  },
  {
    // Canonical complications/functions array on a reference is `functions`,
    // sourced from lookup_watch_functions (same as ReferenceEditor).
    field: 'complications',
    label: 'Complications',
    type: 'lookup-multi',
    lookupCollection: 'lookup_watch_functions',
    paths: ['functions'],
  },
];

/**
 * Apply an updates record that may contain Firestore dot-paths (e.g. 'case.material')
 * to a plain object immutably, so optimistic local state mirrors the nested write
 * that updateDoc performs. Plain keys are set directly.
 *
 * Exported so callers can mirror a bulk write into their own list state (see the
 * `onRefsUpdated` callback).
 */
export function applyDotPathUpdates<T extends Record<string, unknown>>(
  obj: T,
  updates: Record<string, unknown>,
): T {
  const result: Record<string, unknown> = { ...obj };
  for (const [key, value] of Object.entries(updates)) {
    if (!key.includes('.')) {
      result[key] = value;
      continue;
    }
    const parts = key.split('.');
    let cursor = result;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      cursor[part] = { ...((cursor[part] as Record<string, unknown>) ?? {}) };
      cursor = cursor[part] as Record<string, unknown>;
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return result as T;
}

export interface ReferenceBulkActionBarProps {
  brand: WatchBrand;
  /**
   * Full Firestore docs for the current selection. Every action operates on
   * these, so a caller holding only ids must resolve them first (the bar itself
   * never fetches).
   */
  selectedRefs: WatchReference[];
  /**
   * True while the caller is still resolving docs for selected ids. Disables
   * every action so a partial selection can't be written.
   */
  loadingSelection?: boolean;
  /** Appended to the count label, e.g. ' (all)' when a whole brand is selected. */
  selectionLabelSuffix?: string;
  /** Drop the selection. Owned by the caller. */
  onClearSelection: () => void;
  /**
   * Show the "Resync Algolia" action. Off for variant selections — variants are
   * deliberately not indexed in Algolia, so touching them syncs nothing.
   */
  showResync?: boolean;
  /**
   * A bulk update committed. `updates` is keyed by Firestore field path (may
   * contain dot-paths); mirror it into local state with applyDotPathUpdates.
   */
  onRefsUpdated?: (ids: Set<string>, updates: Record<string, unknown>) => void;
  /** A bulk delete committed for these ids. */
  onRefsDeleted?: (ids: Set<string>) => void;
  /**
   * A calibre-enrich or AI-enrich run finished (or its modal closed), so the
   * caller's list may be stale and should be refetched.
   */
  onRefsEnriched?: () => void;
  /**
   * Which parent/variant actions to offer: the listing (parents) gets "Group
   * as variants" + "Ungroup"; a parent's Variants panel gets "Detach from
   * parent". Both refetch through onRefsEnriched.
   */
  groupingActions?: 'listing' | 'variants';
}

/**
 * Floating bulk-action bar for a reference selection, with the modals each
 * action opens (bulk update, calibre enrich, AI enrich, delete).
 *
 * Extracted verbatim from ReferenceBrowser so the reference editor's Variants
 * panel offers the same actions over a parent's children instead of
 * reimplementing them. The bar owns its action state and writes; the CALLER owns
 * the selection and its own list state, and reconciles via the callbacks.
 */
export function ReferenceBulkActionBar({
  brand,
  selectedRefs,
  loadingSelection = false,
  selectionLabelSuffix = '',
  onClearSelection,
  showResync = true,
  onRefsUpdated,
  onRefsDeleted,
  onRefsEnriched,
  groupingActions = 'listing',
}: ReferenceBulkActionBarProps) {
  // Calibre write-path migration: both default ON, so the payload below is
  // unchanged until the cutover deliberately retires a legacy field.
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag(
    'calibre.dualWriteLegacyCalibreString',
  );

  // Bulk resync
  const [isResyncing, setIsResyncing] = useState(false);
  const [resyncProgress, setResyncProgress] = useState(0);
  const [resyncTotal, setResyncTotal] = useState(0);

  // Bulk update
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateField, setBulkUpdateField] = useState('collection');
  const [bulkUpdateValue, setBulkUpdateValue] = useState('');
  // Multi-value (lookup-multi) bulk fields store their selection here instead of
  // bulkUpdateValue. Reset alongside bulkUpdateValue whenever the field changes.
  const [bulkUpdateArrayValue, setBulkUpdateArrayValue] = useState<string[]>([]);

  // Bulk calibre enrichment
  const [showCalibreEnrichModal, setShowCalibreEnrichModal] = useState(false);
  const [calibreEnrichProgress, setCalibreEnrichProgress] = useState(0);
  const [calibreEnrichTotal, setCalibreEnrichTotal] = useState(0);
  const [calibreEnrichRunning, setCalibreEnrichRunning] = useState(false);
  const [calibreEnrichResults, setCalibreEnrichResults] = useState<{
    updated: number;
    skipped: number;
    noCalibre: number;
    unmatched: string[];
    errors: string[];
  } | null>(null);
  const calibreEnrichAbortRef = useRef(false);
  const [showCalibreDb, setShowCalibreDb] = useState(false);

  // Bulk AI enrich (full) over the current selection
  const [showAIEnrichModal, setShowAIEnrichModal] = useState(false);

  // Parent/variant grouping
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showUngroupModal, setShowUngroupModal] = useState(false);

  // Bulk delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  const count = selectedRefs.length;
  const plural = count !== 1 ? 's' : '';

  // Bridge to BulkAIEnrich's unapplied-results guard, so the enrich Modal's ✕
  // and Escape both ask before discarding a batch. Null whenever that tool
  // isn't mounted, in which case the exit just proceeds.
  const enrichExitGuardRef = useRef<((proceed: () => void) => void) | null>(null);
  const guardedEnrichExit = (proceed: () => void) => {
    const guard = enrichExitGuardRef.current;
    if (guard) guard(proceed);
    else proceed();
  };

  // ── Bulk resync to Algolia ──────────────────────────────────────────────────

  const handleBulkResync = async () => {
    const ids = selectedRefs.map(r => r.id);
    setIsResyncing(true);
    setResyncProgress(0);
    setResyncTotal(ids.length);
    let synced = 0;
    let errors = 0;
    try {
      // Touch each reference (update updatedAt) to trigger syncReferenceToAlgolia
      for (const refId of ids) {
        try {
          await updateWatchReference(brand.id, refId, {
            updatedAt: Timestamp.now(),
          });
          synced++;
        } catch {
          errors++;
        }
        setResyncProgress(synced + errors);
      }
      toast.success(`Resynced ${synced} reference${synced !== 1 ? 's' : ''}${errors > 0 ? ` (${errors} failed)` : ''}`);
    } catch (err) {
      console.error('Bulk resync failed:', err);
      toast.error('Resync failed');
    } finally {
      setIsResyncing(false);
      setResyncProgress(0);
      setResyncTotal(0);
    }
  };

  // ── Bulk delete ─────────────────────────────────────────────────────────────

  const handleBulkDelete = async () => {
    setIsDeletingBulk(true);
    try {
      await Promise.all(
        selectedRefs.map(ref =>
          deleteWatchReference(
            brand.id,
            ref.id,
            ref.images?.map(i => i.storagePath) ?? [],
          ),
        ),
      );
      const deletedIds = new Set(selectedRefs.map(r => r.id));
      setShowDeleteModal(false);
      onRefsDeleted?.(deletedIds);
      toast.success(`Deleted ${deletedIds.size} reference${deletedIds.size !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting references:', error);
      toast.error('Failed to delete some references');
    } finally {
      setIsDeletingBulk(false);
    }
  };

  // ── Bulk update ─────────────────────────────────────────────────────────────

  const handleBulkUpdate = async () => {
    setIsBulkUpdating(true);
    try {
      const fieldDef = BULK_UPDATE_FIELDS.find((f) => f.field === bulkUpdateField);
      const paths = fieldDef?.paths ?? [bulkUpdateField];
      const value = bulkUpdateValue.trim();

      // Build the write payload keyed by Firestore field path. updateDoc resolves
      // dot-paths to nested fields; array fields write string[], numbers coerce.
      let updates: Record<string, unknown> = {};
      if (fieldDef?.type === 'lookup-multi') {
        const serialized = fieldDef.multiAsString
          ? bulkUpdateArrayValue.join(', ')
          : bulkUpdateArrayValue;
        for (const p of paths) updates[p] = serialized;
      } else if (fieldDef?.type === 'number') {
        if (value === '') {
          toast.error('Enter a number');
          setIsBulkUpdating(false);
          return;
        }
        const num = Number(value);
        if (Number.isNaN(num)) {
          toast.error('Enter a valid number');
          setIsBulkUpdating(false);
          return;
        }
        for (const p of paths) updates[p] = num;
      } else {
        for (const p of paths) updates[p] = value;
      }

      // Setting the calibre goes through one resolver, which decides both the
      // authoritative `calibreId` link and any legacy fields written beside it.
      // It replaces the generic payload above rather than merging into it: the
      // legacy-string flag has to be able to drop `calibre` entirely, and the
      // generic branch has already written the typed value to that same key.
      if (bulkUpdateField === 'calibre' && value) {
        let calibres: CustomCalibreEntry[] | null = null;
        try {
          calibres = await getCustomCalibres();
        } catch {
          // Non-fatal while the legacy string is still being written — fall
          // through to the typed value below. With the string switched off
          // there is nothing left to fall back to, so this aborts instead.
          calibres = null;
        }

        if (calibres) {
          const built = buildCalibreBulkUpdate(calibres, value, brand, {
            legacyMovement: dualWriteLegacyMovement,
            legacyCalibreString: dualWriteLegacyCalibreString,
          });
          updates = built.updates;
          if (built.resolved && dualWriteLegacyMovement) {
            toast.success(`Enriched with ${built.resolved.entry.name} calibre data`);
          }
        } else if (dualWriteLegacyCalibreString) {
          updates = { calibre: value };
        } else {
          updates = {};
        }

        if (Object.keys(updates).length === 0) {
          // No calibre matched and the legacy string is off, so the write would
          // touch every selected reference without changing anything.
          toast.error(
            `"${value}" matched no calibre — nothing to write with the legacy calibre string switched off`,
          );
          setIsBulkUpdating(false);
          return;
        }
      }

      await Promise.all(
        selectedRefs.map(ref =>
          updateWatchReference(brand.id, ref.id, updates as Partial<WatchReference>),
        ),
      );
      onRefsUpdated?.(new Set(selectedRefs.map(r => r.id)), updates);
      setShowBulkUpdateModal(false);
      setBulkUpdateValue('');
      setBulkUpdateArrayValue([]);
      toast.success(`Updated ${count} reference${plural}`);
    } catch (error) {
      console.error('Error updating references:', error);
      toast.error('Failed to update some references');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // ── Bulk calibre enrichment ─────────────────────────────────────────────────

  const handleBulkCalibreEnrich = async () => {
    setCalibreEnrichRunning(true);
    setCalibreEnrichProgress(0);
    setCalibreEnrichTotal(selectedRefs.length);
    setCalibreEnrichResults(null);
    calibreEnrichAbortRef.current = false;

    const allCalibres = await getCustomCalibres();
    // Resolving in a loop — index the corpus by id once rather than scanning it
    // per reference.
    const calibresById = buildCalibreIdIndex(allCalibres);

    let updated = 0;
    let skipped = 0;
    let noCalibre = 0;
    const unmatchedSet = new Set<string>();
    const errors: string[] = [];

    for (let i = 0; i < selectedRefs.length; i++) {
      if (calibreEnrichAbortRef.current) break;
      const ref = selectedRefs[i];
      setCalibreEnrichProgress(i + 1);

      // What the ref calls its calibre — cache first, legacy string second, so
      // this keeps working once the legacy string stops being written.
      const calibreName = resolveDisplayCalibre(ref);
      if (!calibreName && !ref.calibreId) {
        noCalibre++;
        continue;
      }

      // Find the calibre in the DB. The `calibreId` FK identifies it exactly and
      // wins when present; otherwise the name resolves under the same
      // brand-scoped rule as everywhere else, so a placeholder string or another
      // brand's alias never matches here.
      const match = findRefCalibreEntry(allCalibres, ref, brand, { byId: calibresById });

      if (!match?.data) {
        // A ref linked by FK to a calibre carrying no spec has nothing to report
        // under a name; fall back to the id so the row is still identifiable.
        unmatchedSet.add(calibreName || `calibreId:${ref.calibreId}`);
        skipped++;
        continue;
      }

      try {
        const d = match.data;
        const movementRaw: Record<string, unknown> = {
          baseMovement: d.baseMovement,
          type: d.movementType,
          jewels: d.jewels,
          powerReserveHours: d.powerReserveHours,
          frequencyVph: d.frequencyVph,
          hacking: d.hacking,
          manualWinding: d.manualWinding,
          quickSetDate: d.quickSetDate,
          isChronometer: d.isChronometer,
          isCosc: d.isCosc,
          isGlashuetteRegulation: d.isGlashuetteRegulation,
          isGenevaSeal: d.isGenevaSeal,
          isGrandSeikoStandard: d.isGrandSeikoStandard,
          isMETAS: d.isMETAS,
          isChronograph: d.isChronograph,
          isGMT: d.isGMT,
          isInHouse: d.isInHouse,
          columnWheel: d.columnWheel,
          doubleBarrel: d.doubleBarrel,
          diameterMm: d.diameterMm,
          heightMm: d.heightMm,
          escapement: d.escapement,
          liftAngleDegrees: d.liftAngleDegrees,
          windingDirection: d.windingDirection,
          antiShockSystem: d.antiShockSystem,
          rotorType: d.rotorType,
          hairspring: d.hairspring,
          regulatorSystem: d.regulatorSystem,
          countryOfManufacture: d.countryOfManufacture,
          serviceIntervalYears: d.serviceIntervalYears,
          lignes: d.lignes,
          batteryType: d.batteryType,
          batteryLifeYears: d.batteryLifeYears,
          // Array fields (complications/functions)
          chronograph: d.chronograph,
          date: d.date,
          hands: d.hands,
          additionals: d.additionals,
          astronomical: d.astronomical,
          acoustic: d.acoustic,
          // Durability
          shockResistanceG: d.shockResistanceG,
          antiMagneticAm: d.antiMagneticAm,
          antiMagneticGauss: d.antiMagneticGauss,
          // Other missing fields
          casingDiameterMm: d.casingDiameterMm,
          manualWindingDirection: d.manualWindingDirection,
        };
        // Keep false booleans — only strip undefined/null/empty strings/empty arrays
        const movement = Object.fromEntries(
          Object.entries(movementRaw).filter(([, v]) => {
            if (v === undefined || v === null) return false;
            if (typeof v === 'string' && v === '') return false;
            if (Array.isArray(v) && v.length === 0) return false;
            return true;
          }),
        );

        // Merge with existing movement data so we don't lose fields not in the calibre DB
        const existingMovement = (ref.movement || {}) as Record<string, unknown>;
        const mergedMovement = { ...existingMovement, ...movement };

        // Derive reference-level functions from calibre complication arrays
        const existingFunctions = (ref.functions as string[] | undefined) || [];
        const derivedFunctions = deriveFunctionsFromCalibre(d, existingFunctions);

        const updates: Partial<WatchReference> = {
          calibre: match.name, // Use canonical name
          ...(d.movementType ? { movementType: d.movementType } : {}),
          ...(Object.keys(mergedMovement).length > 0 ? { movement: mergedMovement as WatchReference['movement'] } : {}),
          ...(derivedFunctions.length > 0 ? { functions: derivedFunctions } : {}),
        };

        await updateWatchReference(brand.id, ref.id, updates);
        updated++;
      } catch (err) {
        errors.push(`${ref.reference}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }

    setCalibreEnrichResults({
      updated,
      skipped,
      noCalibre,
      unmatched: Array.from(unmatchedSet).sort(),
      errors,
    });
    setCalibreEnrichRunning(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const bulkUpdateFieldDef = BULK_UPDATE_FIELDS.find(f => f.field === bulkUpdateField);
  const bulkFieldLabel = bulkUpdateFieldDef?.label ?? bulkUpdateField;

  return (
    <>
      {/* ── Floating action bar ─────────────────────────────────────────────── */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <div className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl">
          <span className="text-sm font-medium">
            {count.toLocaleString()} selected{selectionLabelSuffix}
            {loadingSelection && <span className="text-gray-400 ml-1">(loading...)</span>}
          </span>
          <div className="h-4 w-px bg-gray-600" />
          <button
            onClick={onClearSelection}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            Clear
          </button>
          {showResync && (
            <button
              onClick={handleBulkResync}
              disabled={isResyncing || loadingSelection}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2"
            >
              {isResyncing && (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
              )}
              {isResyncing ? `Resyncing ${resyncProgress}/${resyncTotal}` : 'Resync Algolia'}
            </button>
          )}
          <button
            onClick={() => { setBulkUpdateValue(''); setBulkUpdateArrayValue([]); setShowBulkUpdateModal(true); }}
            disabled={loadingSelection}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            Bulk Update
          </button>
          <button
            onClick={() => { setCalibreEnrichResults(null); setShowCalibreEnrichModal(true); }}
            disabled={loadingSelection}
            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            Enrich Calibres
          </button>
          <button
            onClick={() => setShowAIEnrichModal(true)}
            disabled={loadingSelection}
            className="bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            AI Enrich
          </button>
          {groupingActions === 'listing' && (
            <button
              onClick={() => setShowGroupModal(true)}
              disabled={loadingSelection || count < 2 || count > 100}
              title={count < 2 ? 'Select at least two references' : count > 100 ? 'Select at most 100 references' : 'Pick a parent; the rest become its variants'}
              className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              Group as variants
            </button>
          )}
          <button
            onClick={() => setShowUngroupModal(true)}
            disabled={loadingSelection || count > 100}
            title={groupingActions === 'variants' ? 'Make the selected variants top-level references' : 'Detach the selected parents’ variants'}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            {groupingActions === 'variants' ? 'Detach from parent' : 'Ungroup'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={loadingSelection}
            className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {showGroupModal && (
        <GroupAsVariantsDialog
          isOpen
          onClose={() => setShowGroupModal(false)}
          brand={brand}
          selectedRefs={selectedRefs}
          onGrouped={() => {
            onClearSelection();
            onRefsEnriched?.();
          }}
        />
      )}
      {showUngroupModal && (
        <UngroupVariantsDialog
          isOpen
          onClose={() => setShowUngroupModal(false)}
          brand={brand}
          selectedRefs={selectedRefs}
          onUngrouped={() => {
            onClearSelection();
            onRefsEnriched?.();
          }}
        />
      )}

      {/* ── AI Enrich (full) over the selected refs ─────────────────────────── */}
      {/* Backdrop dismissal is off and ✕ / Escape route through the tool's own
          unapplied-results guard: these results cost AI credits to produce and
          live only in the tool's state, so an accidental close is expensive in a
          way that closing the other modals here is not. */}
      <Modal
        isOpen={showAIEnrichModal}
        onClose={() =>
          guardedEnrichExit(() => {
            setShowAIEnrichModal(false);
            onRefsEnriched?.();
          })
        }
        title={`AI Enrich ${count} reference${plural}`}
        size="full"
        dismissOnBackdrop={false}
      >
        {showAIEnrichModal && (
          <BulkAIEnrich
            embedded
            initialBrandId={brand.id}
            initialRefs={selectedRefs}
            onApplied={onRefsEnriched}
            onApplySucceeded={() => setShowAIEnrichModal(false)}
            exitGuardRef={enrichExitGuardRef}
          />
        )}
      </Modal>

      {/* ── Delete confirmation modal ───────────────────────────────────────── */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete References"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-semibold text-red-800">
                You are about to permanently delete {count} reference{plural}
              </p>
              <p className="text-sm text-red-700 mt-1">
                This action cannot be undone. All associated images will also be deleted.
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100">
            {selectedRefs.map(ref => (
              <div key={ref.id} className="px-4 py-2.5 bg-red-50/50">
                <span className="font-medium text-gray-900 text-sm">{ref.reference}</span>
                {(ref.model || ref.productName) && (
                  <span className="text-gray-500 text-sm ml-2">
                    — {ref.model || ref.productName}
                  </span>
                )}
                {ref.collection && (
                  <span className="text-xs text-gray-400 ml-2">({ref.collection})</span>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeletingBulk}
            >
              Cancel
            </Button>
            <button
              onClick={handleBulkDelete}
              disabled={isDeletingBulk}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              {isDeletingBulk && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Delete {count} reference{plural}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Bulk update modal ───────────────────────────────────────────────── */}
      <Modal
        isOpen={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        title={`Bulk Update ${count} Reference${plural}`}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Set a field value on all {count} selected reference{plural}.
            Existing values will be overwritten.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field</label>
            <select
              value={bulkUpdateField}
              onChange={(e) => {
                setBulkUpdateField(e.target.value);
                // Reset both value stores so a stale value from the previously
                // selected field never leaks into the new write.
                setBulkUpdateValue('');
                setBulkUpdateArrayValue([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              {BULK_UPDATE_FIELDS.map(({ field, label }) => (
                <option key={field} value={field}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            {bulkUpdateFieldDef?.type === 'lookup-multi' && bulkUpdateFieldDef.lookupCollection ? (
              <LookupMultiSelect
                label={`Value for "${bulkFieldLabel}"`}
                values={bulkUpdateArrayValue}
                lookupCollection={bulkUpdateFieldDef.lookupCollection}
                onChange={setBulkUpdateArrayValue}
              />
            ) : bulkUpdateFieldDef?.type === 'lookup' && bulkUpdateFieldDef.lookupCollection ? (
              <LookupFieldSelect
                label={`Value for "${bulkFieldLabel}"`}
                value={bulkUpdateValue}
                lookupCollection={bulkUpdateFieldDef.lookupCollection}
                onChange={setBulkUpdateValue}
              />
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value for "{bulkFieldLabel}"
                </label>
                <input
                  type={bulkUpdateFieldDef?.type === 'number' ? 'number' : 'text'}
                  value={bulkUpdateValue}
                  onChange={(e) => setBulkUpdateValue(e.target.value)}
                  placeholder={`Enter ${bulkFieldLabel.toLowerCase()}...`}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  autoFocus
                />
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowBulkUpdateModal(false)}
              disabled={isBulkUpdating}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkUpdate} loading={isBulkUpdating}>
              Apply to {count} reference{plural}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Calibre Database modal ──────────────────────────────────────────── */}
      <CustomCalibreModal
        isOpen={showCalibreDb}
        onClose={() => setShowCalibreDb(false)}
        currentBrandName={brand.displayName || brand.name}
        defaultBrandId={brand.id}
        initialMode='list'
      />

      {/* ── Calibre enrichment modal ───────────────────────────────────────── */}
      <Modal
        isOpen={showCalibreEnrichModal}
        onClose={() => { if (!calibreEnrichRunning) { setShowCalibreEnrichModal(false); onRefsEnriched?.(); } }}
        title="Enrich Calibres from Database"
      >
        <div className='space-y-4'>
          {!calibreEnrichRunning && !calibreEnrichResults && (
            <>
              <p className='text-sm text-gray-600'>
                Look up each selected reference's calibre in the calibre database and populate movement fields (jewels, frequency, power reserve, hacking, base movement, etc.).
              </p>
              <p className='text-sm text-gray-500'>
                {count} reference{plural} selected
              </p>
              <div className='flex gap-2 justify-end'>
                <Button variant='secondary' onClick={() => setShowCalibreEnrichModal(false)}>Cancel</Button>
                <Button onClick={handleBulkCalibreEnrich}>Start Enrichment</Button>
              </div>
            </>
          )}

          {calibreEnrichRunning && (
            <div className='space-y-3'>
              <p className='text-sm text-gray-600'>
                Enriching calibre data... {calibreEnrichProgress}/{calibreEnrichTotal}
              </p>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-indigo-600 h-2 rounded-full transition-all'
                  style={{ width: `${(calibreEnrichProgress / calibreEnrichTotal) * 100}%` }}
                />
              </div>
              <div className='flex justify-end'>
                <Button variant='secondary' onClick={() => { calibreEnrichAbortRef.current = true; }}>
                  Stop
                </Button>
              </div>
            </div>
          )}

          {calibreEnrichResults && (
            <div className='space-y-3'>
              <div className='grid grid-cols-2 gap-2 text-sm'>
                <div className='bg-green-50 rounded-lg p-3'>
                  <div className='text-green-700 font-bold text-lg'>{calibreEnrichResults.updated}</div>
                  <div className='text-green-600'>Updated</div>
                </div>
                <div className='bg-amber-50 rounded-lg p-3'>
                  <div className='text-amber-700 font-bold text-lg'>{calibreEnrichResults.skipped}</div>
                  <div className='text-amber-600'>Not in DB</div>
                </div>
                <div className='bg-gray-50 rounded-lg p-3'>
                  <div className='text-gray-700 font-bold text-lg'>{calibreEnrichResults.noCalibre}</div>
                  <div className='text-gray-500'>No calibre set</div>
                </div>
                {calibreEnrichResults.errors.length > 0 && (
                  <div className='bg-red-50 rounded-lg p-3'>
                    <div className='text-red-700 font-bold text-lg'>{calibreEnrichResults.errors.length}</div>
                    <div className='text-red-600'>Errors</div>
                  </div>
                )}
              </div>

              {calibreEnrichResults.unmatched.length > 0 && (
                <div>
                  <h4 className='text-sm font-medium text-gray-700 mb-2'>
                    Unmatched calibres ({calibreEnrichResults.unmatched.length})
                  </h4>
                  <div className='max-h-48 overflow-y-auto border rounded-lg divide-y'>
                    {calibreEnrichResults.unmatched.map((name) => (
                      <div key={name} className='flex items-center justify-between px-3 py-2 text-sm'>
                        <span className='font-mono text-gray-700'>{name}</span>
                        <div className='flex gap-2'>
                          <button
                            type='button'
                            className='text-xs text-gray-500 hover:text-gray-700'
                            onClick={() => {
                              navigator.clipboard.writeText(name);
                              toast.success(`Copied "${name}"`);
                            }}
                          >
                            Copy
                          </button>
                          <button
                            type='button'
                            className='text-xs text-indigo-600 hover:text-indigo-800 underline'
                            onClick={() => setShowCalibreDb(true)}
                          >
                            Open DB
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {calibreEnrichResults.errors.length > 0 && (
                <details className='text-xs text-red-600'>
                  <summary className='cursor-pointer'>Show errors</summary>
                  <ul className='mt-1 space-y-1'>
                    {calibreEnrichResults.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}

              <div className='flex justify-end gap-2'>
                <Button variant='secondary' onClick={() => {
                  setCalibreEnrichResults(null);
                  handleBulkCalibreEnrich();
                }}>
                  Run Again
                </Button>
                <Button onClick={() => { setShowCalibreEnrichModal(false); onRefsEnriched?.(); }}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
